import { fetchAPI } from './fetchAPI';

// ==================== Promotion Policies ====================

export const getPromotionPolicies = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.isActive !== undefined) queryParams.append('is_active', params.isActive);
  if (params.promptName) queryParams.append('prompt_name', params.promptName);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/promotion-policies${query ? '?' + query : ''}`);
};

export const getPromotionPolicy = async (policyId: string): Promise<any> => {
  return fetchAPI(`/genaiops/promotion-policies/${policyId}`);
};

export const createPromotionPolicy = async (policy: Record<string, any>): Promise<any> => {
  return fetchAPI('/genaiops/promotion-policies', {
    method: 'POST',
    body: JSON.stringify({
      name: policy.name,
      description: policy.description,
      prompt_name: policy.promptName || '*',
      trigger: policy.trigger || 'always',
      dataset_id: policy.datasetId,
      quality_gate_config: policy.qualityGateConfig,
      evaluation_config: policy.evaluationConfig,
    }),
  });
};

export const updatePromotionPolicy = async (policyId: string, updates: Record<string, any>): Promise<any> => {
  const body: Record<string, any> = {};
  if (updates.name !== undefined) body.name = updates.name;
  if (updates.description !== undefined) body.description = updates.description;
  if (updates.promptName !== undefined) body.prompt_name = updates.promptName;
  if (updates.trigger !== undefined) body.trigger = updates.trigger;
  if (updates.datasetId !== undefined) body.dataset_id = updates.datasetId;
  if (updates.qualityGateConfig !== undefined) body.quality_gate_config = updates.qualityGateConfig;
  if (updates.evaluationConfig !== undefined) body.evaluation_config = updates.evaluationConfig;
  if (updates.isActive !== undefined) body.is_active = updates.isActive;

  return fetchAPI(`/genaiops/promotion-policies/${policyId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const deletePromotionPolicy = async (policyId: string): Promise<any> => {
  return fetchAPI(`/genaiops/promotion-policies/${policyId}`, {
    method: 'DELETE',
  });
};

// ==================== LLM Provider Credentials ====================

export const getCredentials = async (): Promise<any> => {
  return fetchAPI('/genaiops/credentials');
};

export const getCredential = async (credentialId: string): Promise<any> => {
  return fetchAPI(`/genaiops/credentials/${credentialId}`);
};

export const createCredential = async (data: Record<string, any>): Promise<any> => {
  return fetchAPI('/genaiops/credentials', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateCredential = async (credentialId: string, updates: Record<string, any>): Promise<any> => {
  return fetchAPI(`/genaiops/credentials/${credentialId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

export const deleteCredential = async (credentialId: string): Promise<any> => {
  return fetchAPI(`/genaiops/credentials/${credentialId}`, {
    method: 'DELETE',
  });
};

export const getAvailableModels = async (): Promise<any> => {
  return fetchAPI('/genaiops/credentials/available-models');
};

export const getAwsAccounts = async (): Promise<any> => {
  return fetchAPI('/genaiops/credentials/aws-accounts');
};

export const autoConfigureBedrock = async (): Promise<any> => {
  return fetchAPI('/genaiops/credentials/auto-configure-bedrock', {
    method: 'POST',
  });
};

export const testCredentialConnection = async (data: Record<string, any>): Promise<any> => {
  return fetchAPI('/genaiops/credentials/test-connection', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const testSavedCredential = async (credentialId: string): Promise<any> => {
  return fetchAPI(`/genaiops/credentials/${credentialId}/test`, {
    method: 'POST',
  });
};

// ==================== Bedrock Quota Monitoring ====================

export const getBedrockQuotaStatus = async (credentialId: string, options: Record<string, any> = {}): Promise<any> => {
  return fetchAPI(`/genaiops/bedrock/quota?credential_id=${credentialId}`, options);
};

export const checkBedrockQuotaAvailable = async (): Promise<any> => {
  return fetchAPI('/genaiops/bedrock/quota/check');
};

// ==================== Bedrock Prompt Sync ====================

export const getBedrockSyncStatus = async (): Promise<any> => {
  return fetchAPI('/genaiops/bedrock/sync/status');
};

export const listBedrockPrompts = async (includeTracked: boolean = true): Promise<any> => {
  return fetchAPI(`/genaiops/bedrock/prompts?include_tracked=${includeTracked}`);
};

export const getBedrockPromptDetails = async (bedrockPromptId: string): Promise<any> => {
  return fetchAPI(`/genaiops/bedrock/prompts/${bedrockPromptId}`);
};

export const importBedrockPrompt = async (bedrockPromptId: string, options: Record<string, any> = {}): Promise<any> => {
  return fetchAPI(`/genaiops/bedrock/prompts/${bedrockPromptId}/import`, {
    method: 'POST',
    body: JSON.stringify({
      environment: options.environment || 'development',
      labels: options.labels || [],
      auto_track: options.auto_track !== false,
    }),
  });
};

export const getBedrockMappings = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.has_external_changes !== undefined) {
    queryParams.append('has_external_changes', params.has_external_changes);
  }
  if (params.sync_direction) {
    queryParams.append('sync_direction', params.sync_direction);
  }
  const query = queryParams.toString();
  return fetchAPI(`/genaiops/bedrock/mappings${query ? '?' + query : ''}`);
};

export const syncBedrockMapping = async (mappingId: string, direction: string = 'pull'): Promise<any> => {
  return fetchAPI(`/genaiops/bedrock/mappings/${mappingId}/sync`, {
    method: 'POST',
    body: JSON.stringify({ direction }),
  });
};

export const resolveBedrockConflict = async (mappingId: string, resolution: string): Promise<any> => {
  return fetchAPI(`/genaiops/bedrock/mappings/${mappingId}/resolve-conflict`, {
    method: 'POST',
    body: JSON.stringify({ resolution }),
  });
};

export const getExternalChanges = async (): Promise<any> => {
  return fetchAPI('/genaiops/bedrock/external-changes');
};

export const validateBedrockCredentials = async (): Promise<any> => {
  return fetchAPI('/genaiops/bedrock/sync/validate-credentials', {
    method: 'POST',
  });
};

export const getBedrockSyncStats = async (hours: number = 24): Promise<any> => {
  return fetchAPI(`/genaiops/bedrock/sync/stats?hours=${hours}`);
};

export const getReconciliationReport = async (): Promise<any> => {
  return fetchAPI('/genaiops/bedrock/reconciliation-report');
};

export const runReconciliation = async (): Promise<any> => {
  return fetchAPI('/genaiops/bedrock/reconciliation-report/run', {
    method: 'POST',
  });
};

export const syncAllPending = async (): Promise<any> => {
  return fetchAPI('/genaiops/bedrock/sync-all-pending', {
    method: 'POST',
  });
};

export const syncPromptToBedrock = async (promptId: string): Promise<any> => {
  const endpoint = `/genaiops/bedrock/sync-prompt/${promptId}`;
  return fetchAPI(endpoint, {
    method: 'POST',
  });
};

// ==================== API Keys ====================

export const bulkRevokeApiKeys = async (ids: string[]): Promise<any> => {
  return fetchAPI('/genaiops/api-keys/bulk', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
};

export const assignApiKeyProject = async (keyId: string, projectId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/api-keys/${keyId}`, {
    method: 'PATCH',
    body: JSON.stringify({ project_id: projectId }),
  });
};

export const bulkAssignApiKeyProject = async (ids: string[], projectId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/api-keys/bulk-assign-project', {
    method: 'PATCH',
    body: JSON.stringify({ ids, project_id: projectId }),
  });
};

export const assignApiKeyWorkload = async (keyId: string, workloadId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/api-keys/${keyId}`, {
    method: 'PATCH',
    body: JSON.stringify({ workload_id: workloadId }),
  });
};

export const bulkAssignApiKeyWorkload = async (ids: string[], workloadId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/api-keys/bulk-assign-workload', {
    method: 'PATCH',
    body: JSON.stringify({ ids, workload_id: workloadId }),
  });
};
